import type { ReactNode } from 'react'

/**
 * One labelled documents section — an icon, a name, a line saying whose paperwork this is, and the control
 * that acts on it.
 *
 * The three sections (the driver's own files, the owner's, the vehicle's) had three hand-rolled header rows
 * between them, and the control that fetches a set sat BELOW its heading, which at full width left a button
 * alone on a line with nothing beside it. Here the action is on the heading row, where a wide card has the
 * room for it, and there is one row to change when the next kind of document arrives.
 *
 * The subtitle is not decoration: "owner documents" and "vehicle documents" are the paperwork of records the
 * driver is ATTACHED to, not files this driver handed in, and an operator looking for a missing licence needs
 * to know which of those they are reading.
 */
export const DocumentSection = ({
    icon,
    title,
    hint,
    action,
    children,
}: {
    icon: ReactNode
    title: string
    /** Whose paperwork this is, in a few words. */
    hint?: string
    /** The control for this section, on the heading row. */
    action?: ReactNode
    children: ReactNode
}) => (
    <section>
        <div className="mb-3 flex items-center gap-3">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-default text-foreground-muted">{icon}</span>
            <div className="min-w-0 flex-1">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">{title}</h3>
                {hint && <p className="truncate text-[11px] text-foreground-muted">{hint}</p>}
            </div>
            {action}
        </div>
        {children}
    </section>
)
